import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Alert,
  CircularProgress,
  Collapse,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocationOn as LocationIcon,
  Warehouse as WarehouseIcon,
  ViewModule as ZoneIcon,
  ViewList as AisleIcon,
  Inventory as BinIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  useTSQLocationList,
  useTSMDeactivateLocation,
  useTSMReactivateLocation,
} from "../../hooks/useTSLocations";
import { ListRequestFilter } from "../../types/common/commonTypes";
import { LocationFull, LocationType } from "../../types/locationTypes";
import { paginationDefaults } from "../../config/settingsDefaults";

export const PageLocationList: React.FC = () => {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("active");
  const [pagination, setPagination] = useState({
    page: 0,
    size: paginationDefaults.startingRowOption,
  });
  const [error, setError] = useState<string | null>(null);
  const [searchFilters, setSearchFilters] = useState<ListRequestFilter[][]>([
    [],
  ]);

  const [orderBy, setOrderBy] = useState<string>("location_path");
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  const [expandedLocations, setExpandedLocations] = useState<Set<string>>(
    new Set(),
  );

  const {
    data: locsResponse,
    isLoading,
    error: queryError,
    isPlaceholderData,
  } = useTSQLocationList({
    skip: -1,
    limit: -1,
    active_status: statusFilter,
    filters: searchFilters,
  });

  const deactivateLocMutation = useTSMDeactivateLocation();
  const reactivateLocMutation = useTSMReactivateLocation();

  const locations = locsResponse?.items || [];
  const totalCount = locsResponse?.total || 0;

  const handleDeactivate = async (id: string) => {
    if (!window.confirm("Are you sure you want to deactivate this Location?")) {
      return;
    }

    try {
      await deactivateLocMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to deactivate Location:", err);
      setError(err.response?.data?.detail || "Failed to deactivate Location");
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      await reactivateLocMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to reactivate Location:", err);
      setError(err.response?.data?.detail || "Failed to reactivate Location");
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPagination((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const getSortValue = (location: LocationFull, property: string): any => {
    switch (property) {
      case "name":
        return location.name?.toLowerCase() || "";
      case "top_location_name":
        return location.top_location_name.toLowerCase();
      case "location_type":
        return location.location_type?.toLowerCase() || "";
      case "status":
        return location.is_record_active ? "active" : "inactive";
      case "created_at":
        return new Date(location.created_at || "");
      default:
        return "";
    }
  };

  const getLocationIcon = (locationType: string) => {
    switch (locationType?.toLowerCase()) {
      case "facility":
        return <WarehouseIcon fontSize="small" />;
      case "room":
        return <ZoneIcon fontSize="small" />;
      case "shelf":
        return <AisleIcon fontSize="small" />;
      case "bin":
        return <BinIcon fontSize="small" />;
      default:
        return <LocationIcon fontSize="small" />;
    }
  };

  const getLocationTypeColor = (locationType: string) => {
    switch (locationType?.toLowerCase()) {
      case "facility":
        return "primary";
      case "room":
        return "secondary";
      case "shelf":
        return "info";
      case "bin":
        return "success";
      default:
        return "default";
    }
  };

  const filteredAndSortedLocations = useMemo(() => {
    const filtered = locations.filter((loc) => {
      const matchesSearch =
        searchTerm === "" ||
        loc.name.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesSearch;
    });

    if (orderBy) {
      const sorted = [...filtered].sort((a, b) => {
        const aValue = getSortValue(a, orderBy);
        const bValue = getSortValue(b, orderBy);

        if (aValue === bValue) return 0;

        if (order === "asc") {
          return aValue < bValue ? -1 : 1;
        } else {
          return aValue > bValue ? -1 : 1;
        }
      });
      const start = pagination.size * pagination.page;
      const end = start + pagination.size;
      return sorted.slice(start, end);
    } else {
      const start = pagination.size * pagination.page;
      const end = start + pagination.size;
      return filtered.slice(start, end);
    }
  }, [locations, searchTerm, orderBy, order, pagination]);

  if (isLoading && locations.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Location Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/locations/form?mode=create")}
        >
          Create Location
        </Button>
      </Stack>

      {(error || queryError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || queryError?.message || "An unknown error occurred"}
        </Alert>
      )}

      {isPlaceholderData && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading updated data...
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="Search"
            placeholder="Search location by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
            sx={{ minWidth: 300 }}
          />

          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              label="Status"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {/* <TextField
          fullWidth
          placeholder="Search locations by code, name, type, or path..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        /> */}
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === "name" ? order : false}>
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={orderBy === "name" ? order : "asc"}
                  onClick={() => handleSort("name")}
                >
                  Location Name
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "location_type" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "location_type"}
                  direction={orderBy === "location_type" ? order : "asc"}
                  onClick={() => handleSort("location_type")}
                >
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "top_location_name" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "top_location_name"}
                  direction={orderBy === "top_location_name" ? order : "asc"}
                  onClick={() => handleSort("top_location_name")}
                >
                  Top Level Location
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "status" ? order : false}>
                <TableSortLabel
                  active={orderBy === "status"}
                  direction={orderBy === "status" ? order : "asc"}
                  onClick={() => handleSort("status")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "created_at" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "created_at"}
                  direction={orderBy === "created_at" ? order : "asc"}
                  onClick={() => handleSort("created_at")}
                >
                  Created
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && locations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredAndSortedLocations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    No Locations found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedLocations.map((location) => (
                <TableRow key={location.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {location.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {location.location_type && (
                      <Box display="flex" gap={2} alignItems="center">
                        {getLocationIcon(location.location_type)}
                        <Chip
                          label={location.location_type}
                          size="small"
                          variant="outlined"
                          color={getLocationTypeColor(location.location_type)}
                          clickable={false}
                          onClick={() => {}}
                        />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {location.top_location_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={location.is_record_active ? "Active" : "Inactive"}
                      color={location.is_record_active ? "success" : "default"}
                      size="small"
                      clickable={false}
                      onClick={() => {}}
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(location.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(
                              `/locations/form?mode=view&id=${location.id}`,
                            )
                          }
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Edit Location">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(
                              `/locations/form?mode=edit&id=${location.id}`,
                            )
                          }
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                      {!location.is_record_active ? (
                        <Tooltip title="Reactivate Location">
                          <IconButton
                            size="small"
                            onClick={() => handleReactivate(location.id)}
                            color="success"
                            disabled={reactivateLocMutation.isPending}
                          >
                            <RestoreIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Deactivate Location">
                          <IconButton
                            size="small"
                            onClick={() => handleDeactivate(location.id)}
                            color="error"
                            disabled={deactivateLocMutation.isPending}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
      </TableContainer>
    </Box>
  );
};

// export default PageLocationList;
